import { useContext } from "react";
import { AuthContext } from "@/components/AuthProvider";
import { trackEvent } from "@/lib/analytics";

type ErrorType = "api_error" | "form_error" | "load_error";

/**
 * Códigos de erro de lista fechada (GR-712, RN-05).
 *
 * Nunca enviar o texto de erro exibido na tela: a mensagem devolvida pelo Keycloak
 * embute o corpo cru da resposta da API, que num 409 pode conter o e-mail digitado.
 * Só valores desta lista podem viajar no evento.
 */
export type AnalyticsErrorCode =
  | "usuario_ja_existe"
  | "erro_rede"
  | "sem_gerador_vinculado"
  | "sem_estabelecimento_selecionado"
  // GR-637: usuário gravado, mas o diff de perfis não foi aplicado. Literal
  // fixo, sem nada do dado digitado — mede quantas criações caem no estado
  // parcial, que é invisível nos códigos http_* (o POST devolveu 2xx).
  | "perfis_nao_aplicados"
  | "perfis_carregando"
  | "campo_nome"
  | "campo_sobrenome"
  | "campo_email"
  | "campo_telefone"
  | "campo_senha"
  | `http_${number}`;

/** Parâmetros adicionais do evento (ex.: categoria/titulo_artigo, form_step). */
type ExtraParams = Record<string, string | number | boolean>;

/** Eventos de negócio de alto valor do portal (GR-557 + GR-712) — ver catálogo em docs/GA4_ANALYTICS_ARCHITECTURE.md. */
export const useAnalytics = () => {
  // RN-07: nenhum evento é enviado antes de a verificação de acesso terminar —
  // sem isso o registro chega sem perfil/gerador e contamina a segmentação.
  // Fora do AuthProvider o contexto é undefined; nesse caso não registramos nada.
  const auth = useContext(AuthContext);
  const isReady = auth != null && !auth.isLoading;

  const track = (name: string, params?: Record<string, unknown>) => {
    if (!isReady) return;
    trackEvent(name, params);
  };

  /**
   * RN-06: chamar apenas após a confirmação de que o arquivo foi entregue.
   * `mtrCount` é opcional porque a contagem vem de uma chamada separada
   * (resumo do lote) que pode falhar sem impedir o download — nesse caso o
   * download ainda é registrado, só sem a quantidade (GR-712).
   */
  const trackMtrDownload = ({ mtrCount }: { mtrCount?: number } = {}) => {
    track("mtr_download", mtrCount != null ? { mtr_count: mtrCount } : undefined);
  };

  const trackFinancialRuleView = ({
    collectionId,
  }: {
    collectionId: string;
  }) => {
    track("financial_rule_view", { collection_id: collectionId });
  };

  /** `pagePath` default é a rota atual — passe explicitamente só se precisar sobrescrever. */
  const trackButtonClick = ({
    buttonLabel,
    pagePath,
    ...extra
  }: {
    buttonLabel: string;
    pagePath?: string;
  } & ExtraParams) => {
    track("button_click", {
      button_label: buttonLabel,
      page_path: pagePath ?? window.location.pathname,
      ...extra,
    });
  };

  const trackModalOpen = ({ modalName }: { modalName: string }) => {
    track("modal_open", { modal_name: modalName });
  };

  /**
   * RN-05: `errorCode` é de lista fechada e chega ao GA4 no parâmetro
   * `error_message` (nome mantido do GR-557 para não recadastrar no GA4).
   * Nunca passar texto de erro da API nem valor digitado pelo usuário.
   */
  const trackError = ({
    errorType,
    errorCode,
    pagePath,
  }: {
    errorType: ErrorType;
    errorCode: AnalyticsErrorCode;
    pagePath?: string;
  }) => {
    track("error_occurred", {
      error_type: errorType,
      error_message: errorCode,
      page_path: pagePath ?? window.location.pathname,
    });
  };

  return {
    trackMtrDownload,
    trackFinancialRuleView,
    trackButtonClick,
    trackModalOpen,
    trackError,
  };
};
